import ExcelJS from "exceljs";
import { htmlToText } from "html-to-text";
import { getFormattedUnit } from "config";

export const handleUploadLabourExcel = async (
  event,
  values,
  setFieldValue,
  rfqData,
  setExcelLoading,
  setLoadingMessage,
  enqueueSnackbar,
  calculateOverallTotal,
  calculateOverallTotalWithoutGST,
  setTotalProposal,
  miscellaneousCosts,
  setExcelError
) => {
  try {
    setExcelLoading(true);
    setLoadingMessage("Processing uploaded labour rates...");

    const file = event.target.files[0];
    if (!file) return;

    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const sheet =
      workbook.getWorksheet("labour_rates") || workbook.worksheets[0];

    if (!sheet) {
      enqueueSnackbar("Invalid labour Excel sheet", { variant: "error" });
      return;
    }

    const updatedItems = [...values.labourItems];
    const errors = [];

    sheet.eachRow((row, rowNumber) => {
      // Skip header rows
      if (rowNumber <= 2) return;
    
      const rawLabourId = row.getCell(2).value;
      const rateCell = row.getCell(6).value;
      const gstCell = row.getCell(7).value;
    
      // Skip fully empty rows
      if (!rawLabourId && !rateCell && !gstCell) return;
    
      // Normalize Labour ID
      const labourId =
        typeof rawLabourId === "object"
          ? rawLabourId.text || rawLabourId.richText?.[0]?.text
          : rawLabourId;
    
      const rate = parseFloat(rateCell) || 0;
      let gst = parseFloat(gstCell) || 0;
    
      // Accept GST as-is (18 or 0.18)
      // if (gst > 0 && gst <= 1) {
      //   gst = gst * 100;
      // }
    
      const remarks = row.getCell(8).value || "";
    
      const index = updatedItems.findIndex(
        (item) => item.id?.toString() === labourId?.toString()
      );
    
      if (index === -1) {
        errors.push(`Invalid Labour ID at row ${rowNumber}`);
        return;
      }
    
      const qty = parseFloat(updatedItems[index].quantity || 0);
      const totalExclGst = qty * rate;
      const totalAmount = totalExclGst * (1 + gst / 100);
    
      updatedItems[index] = {
        ...updatedItems[index],
        labourRate: rate,
        gst,
        remarks,
        totalExclGst,
        totalAmount,
      };
    });
    

    if (errors.length) {
      enqueueSnackbar(errors.join(", "), { variant: "error" });
      return;
    }

    setFieldValue("labourItems", updatedItems);

    const newTotal = calculateOverallTotal(updatedItems, miscellaneousCosts);
    setTotalProposal(newTotal);
    calculateOverallTotalWithoutGST(updatedItems, miscellaneousCosts);

    enqueueSnackbar("Rates uploaded successfully", {
      variant: "success",
    });
  } catch (error) {
    console.error("Labour Excel upload error:", error);
    setExcelError("Failed to upload labour Excel. Please check the file.");
    enqueueSnackbar("Failed to upload labour Excel", { variant: "error" });
  } finally {
    setExcelLoading(false);
    setLoadingMessage("");
  }
};

export const handleDownloadLabourExcel = async (
  rfqData,
  labourItems,
  setExcelLoading,
  setLoadingMessage,
  setExcelError
) => {
  try {
    setExcelLoading(true);
    setLoadingMessage("Preparing labour requirement sheet...");

    // Load Excel template from public folder
    const response = await fetch(
      "/assets/general/RFQ_labour_rate_upload_template.xlsx"
    );

    if (!response.ok) {
      throw new Error("Excel template not found");
    }

    const arrayBuffer = await response.arrayBuffer();

    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    // Get sheet safely
    const sheet =
      workbook.getWorksheet("labour_rates") || workbook.worksheets[0];

    // Template configuration
    const HEADER_ROW_1 = 1;
    const HEADER_ROW_2 = 2;
    const DATA_START_ROW = 3;
    const TEMPLATE_DATA_ROW = 3;


    // Clear ONLY existing DATA values (row 3 onwards)
    // Do NOT touch row 1 or row 2
    for (let rowIndex = DATA_START_ROW; rowIndex <= sheet.rowCount; rowIndex++) {
      const row = sheet.getRow(rowIndex);
      for (let col = 1; col <= 8; col++) {
        row.getCell(col).value = "";
      }
    }
    

    // Build unit map
    const labourUnitMap = new Map(
      rfqData?.labourDetails?.map((ld) => [
        ld._id?.toString(),
        ld.unit || "Nos",
      ])
    );

    // Data source
    const rowsSource =
      labourItems?.length > 0 ? labourItems : rfqData?.labourDetails || [];

    // Fill data starting STRICTLY from row 3
    rowsSource.forEach((item, index) => {
      const targetRowNumber = DATA_START_ROW + index;
      const labourId = (item.id || item._id)?.toString();
    
      let row = sheet.getRow(targetRowNumber);
    
      if (targetRowNumber > sheet.rowCount) {
        row = sheet.addRow([]);
    
        const templateRow = sheet.getRow(TEMPLATE_DATA_ROW);
        templateRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const newCell = row.getCell(colNumber);
          newCell.style = { ...cell.style };
          newCell.protection = { ...cell.protection };
    
          if (cell.value?.formula) {
            newCell.value = { ...cell.value };
          }
        });
      }
    
      row.getCell(1).value = index + 1;
      row.getCell(2).value = labourId;
      row.getCell(3).value = htmlToText(item.skill || "");
      row.getCell(4).value = item.quantity || "";
      row.getCell(5).value = labourUnitMap.get(labourId) || "";
      row.getCell(6).value = item.labourRate || "0";
      // GST column is percentage formatted
      // row.getCell(7).value = item.gst;
      row.getCell(7).value = item.gst ? `${item.gst}%` : `18%`;
      row.getCell(8).value = item.remarks || "";
      row.getCell(9).value = {
        formula: `IFERROR(ROUNDDOWN(D${targetRowNumber} * F${targetRowNumber}, 0), "")`
      };
      
      // GST Amount (J) = Basic Amount * GST %
      row.getCell(10).value = {
        formula: `IFERROR(ROUNDDOWN(I${targetRowNumber} * G${targetRowNumber}, 0), "")`
      };
      
      // Total Amount (K) = Basic + GST
      row.getCell(11).value = {
        formula: `IFERROR(I${targetRowNumber} + J${targetRowNumber}, "")`
      };
      row.commit();
    });
    

    // Re-apply protection
    sheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      row.getCell(6).protection = { locked: false };
      row.getCell(7).protection = { locked: false };
      row.getCell(8).protection = { locked: false };
    });

    await sheet.protect("Neevay", {
      selectLockedCells: false,
      selectUnlockedCells: true,
    });

    // Download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `RFQ_${rfqData?.rfqId}_labour_upload.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Labour Excel download error:", error);
    if (typeof setExcelError === "function") {
      setExcelError("Failed to prepare labour requirement Excel.");
    }
  } finally {
    setExcelLoading(false);
    setLoadingMessage("");
  }
};
